import React, { useState, useEffect, useRef } from 'react';
import style from './Reporting.module.css';
import { Table, Pagination, Spin, message, Button, Dropdown, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormInput } from '../../components/Basic/FormInput';
import * as Action from '../../store/slices/reporting';
import { connect } from 'react-redux';
import Header from '../../components/Header/header';
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';
import * as XLSX from 'xlsx';

interface GetData {
    SearchFilter: (payload: any) => unknown;
    ExportFile: (payload: any) => unknown;
    Reporting_Red: any;
}

interface Form {
    StartDate: string;
    EndDate: string;
    CampaignId: string;
}

interface ActiveFilters {
    startDate: string;
    endDate: string;
    campaignIds: number[];
}

const validationSchema = yup.object().shape({
    StartDate: yup.string().optional(),
    EndDate: yup.string().optional(),
    CampaignId: yup.string().optional(),
});

const MultiViewTable: React.FC<GetData> = ({ SearchFilter, Reporting_Red, ExportFile }) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(9);
    const [spinning, setSpinning] = useState(false);
    const [tableData, setTableData] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);

    // use a ref so the page-change effect always reads the latest filters
    const filtersRef = useRef<ActiveFilters>({ startDate: '', endDate: '', campaignIds: [] });

    useEffect(() => {
        const status = Reporting_Red?.dataSingle?.[0]?.res?.status;
        if (status === '1004') { localStorage.clear(); navigate('/'); }
    }, [Reporting_Red]);

    const { control, formState: { errors }, handleSubmit, getValues, setValue } = useForm<Form>({
        defaultValues: { StartDate: '', EndDate: '', CampaignId: '' },
        mode: 'onChange',
        resolver: yupResolver(validationSchema) as any,
    });

    const parseCampaignIds = (raw: string): number[] =>
        (raw || '').split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));

    const fetchData = async (filters: ActiveFilters, page: number, size: number) => {
        setSpinning(true);
        try {
            const response: any = await SearchFilter({
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined,
                campaignIds: filters.campaignIds.length > 0 ? filters.campaignIds : undefined,
                isExport: false,
                pageIndex: page - 1,
                pageRecord: size,
            });

            if (response?.status === '1004') { localStorage.clear(); navigate('/'); return; }
            if (response?.status === '1005') { message.warning('No data available'); setTableData([]); setTotalRecords(0); return; }

            if (response?.data) {
                setTableData(response.data);
                setTotalRecords(response.noOfRecord ?? response.data.length);
            } else {
                setTableData([]);
                setTotalRecords(0);
            }
        } catch {
            message.error('Failed to fetch data');
        } finally {
            setSpinning(false);
        }
    };

    // single effect — fires on page/size change and on initial mount
    useEffect(() => {
        fetchData(filtersRef.current, currentPage, pageSize);
    }, [currentPage, pageSize]);

    const onSearch = (data: Form) => {
        const filters: ActiveFilters = {
            startDate: data.StartDate,
            endDate: data.EndDate,
            campaignIds: parseCampaignIds(data.CampaignId),
        };
        filtersRef.current = filters;   // update ref synchronously before fetch
        setCurrentPage(1);              // if already page 1, effect won't re-fire
        fetchData(filters, 1, pageSize);
    };

    const FilterClear = () => {
        setValue('StartDate', '');
        setValue('EndDate', '');
        setValue('CampaignId', '');
        const empty: ActiveFilters = { startDate: '', endDate: '', campaignIds: [] };
        filtersRef.current = empty;
        setCurrentPage(1);
        fetchData(empty, 1, pageSize);
    };

    const handleExport = async (format: 'csv' | 'xls') => {
        setSpinning(true);
        const values = getValues();
        try {
            const response: any = await ExportFile({
                startDate: values.StartDate || undefined,
                endDate: values.EndDate || undefined,
                campaignIds: parseCampaignIds(values.CampaignId),
                isExport: true,
                pageIndex: 0,
                pageRecord: 500,
            });

            if (response?.csvData || response?.data) {
                const data = response.csvData || response.data;
                
                if (format === 'csv') {
                    // Export as CSV
                    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    const url = URL.createObjectURL(blob);
                    link.href = url;
                    link.download = `Campaign_Audit_Report_${new Date().toISOString().split('T')[0]}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    message.success('CSV file downloaded successfully!');
                } else {
                    // Export as XLS (Excel)
                    // If data is CSV string, parse it first
                    let jsonData = [];
                    if (typeof data === 'string') {
                        // Parse CSV to JSON
                        const lines = data.split('\n');
                        const headers = lines[0].split(',');
                        jsonData = lines.slice(1).filter(line => line.trim()).map(line => {
                            const values = line.split(',');
                            const obj: any = {};
                            headers.forEach((header, index) => {
                                obj[header.trim()] = values[index]?.trim() || '';
                            });
                            return obj;
                        });
                    } else if (Array.isArray(data)) {
                        jsonData = data;
                    }

                    // Create workbook and worksheet
                    const worksheet = XLSX.utils.json_to_sheet(jsonData);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Campaign Report');

                    // Generate Excel file
                    XLSX.writeFile(workbook, `Campaign_Audit_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
                    message.success('Excel file downloaded successfully!');
                }
            } else {
                message.error('No data to export');
            }
        } catch (error) {
            console.error('Export error:', error);
            message.error('Failed to export data');
        } finally {
            setSpinning(false);
        }
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Export as CSV',
            onClick: () => handleExport('csv'),
        },
        {
            key: 'xls',
            label: 'Export as Excel (XLS)',
            onClick: () => handleExport('xls'),
        },
    ];

    const allColumns = [
        { title: 'Campaign Id', dataIndex: 'campaignId', key: 'campaignId', width: 80 },
        { title: 'Campaign Name', dataIndex: 'campaignName', key: 'campaignName', width: 150 },
        { title: 'Start Date', dataIndex: 'startDate', key: 'startDate', width: 120 },
        { title: 'End Date', dataIndex: 'endDate', key: 'endDate', width: 100 },
        { title: 'No Of Winners', dataIndex: 'noOfWinners', key: 'noOfWinners', width: 100 },
        { title: 'Status', dataIndex: 'campaignStatus', key: 'campaignStatus', width: 100 },
        { title: 'Created By', dataIndex: 'createdByName', key: 'createdByName', width: 100 },
        { title: 'Approved By', dataIndex: 'approvedByName', key: 'approvedByName', width: 100 },
    ];

    return (
        <>
            <Header />
            <Spin spinning={spinning} fullscreen />
            <div className={style.Route}>
                <h5>Reporting</h5>
            </div>
            <form className={style.form} onSubmit={handleSubmit(onSearch)}>
                <div className={style.SearchTable}>
                    <div className={style.FormSetting}>
                        <div className={style.field}>
                            <FormInput Label='Start Date' placeholder='Start Date'
                                classInput={style.Input} className={style.inputContainer}
                                labelClass={style.Label} classError={style.Error}
                                name='StartDate' type='date' errors={errors} control={control} />
                        </div>
                        <div className={style.field}>
                            <FormInput Label='End Date' placeholder='End Date'
                                classInput={style.Input} className={style.inputContainer}
                                labelClass={style.Label} classError={style.Error}
                                name='EndDate' type='date' errors={errors} control={control} />
                        </div>
                        <div className={style.field}>
                            <FormInput Label='Campaign ID(s)' placeholder='e.g. 1,2,3'
                                classInput={style.Input} className={style.inputContainer}
                                labelClass={style.Label} classError={style.Error}
                                name='CampaignId' errors={errors} control={control} />
                        </div>
                    </div>
                    <div className={style.btnSetting}>
                        <Button type='primary' htmlType='submit' className={style.buttonSignIn}>Search</Button>
                        <Button type='primary' className={style.buttonSignIn} onClick={FilterClear}>Clear</Button>
                    </div>
                </div>
            </form>
            <div className={style.btnSetting}>
                {hasPermission(PERMISSIONS.AUDIT_REPORT_DOWNLOAD) && (
                    <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                        <Button className={style.buttonSignIn}>
                            <Space>
                                Export
                                <DownOutlined />
                            </Space>
                        </Button>
                    </Dropdown>
                )}
            </div>
            <div className={style.container}>
                <Table
                    columns={allColumns}
                    dataSource={tableData.map((item: any, i: number) => ({ ...item, key: item.campaignId ?? i }))}
                    pagination={false}
                    size="small"
                />
                <Pagination
                    align="end"
                    showTotal={(total) => `Total ${total} items`}
                    current={currentPage}
                    total={totalRecords}
                    pageSize={pageSize}
                    pageSizeOptions={[9, 20, 50]}
                    showSizeChanger
                    onChange={(page, newSize) => {
                        setCurrentPage(page);
                        setPageSize(newSize);
                    }}
                    style={{ marginTop: 16 }}
                />
            </div>
        </>
    );
};

function mapStateToProps(state: any) {
    return { Reporting_Red: state.Reporting_Red };
}

export default connect(mapStateToProps, Action)(MultiViewTable);
